﻿import { FIQAssist } from "../app";
import { Injectable, Inject } from "@fiqas/angular-utils";
import { LogService } from "./logService";

@Injectable({
    module: FIQAssist,
    selector: SessionService.selector
})
/**
 * Session class, stores values as JSON in local browser storage.
 */
export class SessionService {
    public static selector = "Session"

    private sessionprefix: string = "Fiqassist.Session";
    constructor(
        @Inject() private logService: LogService
    ) {

    }

    /**
     * Get a value by key.
     * @param {string} key Key of the value to get.
     * @returns T Item from session or null if not found.
     */
    public getValue<T>(key: string): T {
        var item = this.retrieve<T>(key);
        if (item != null)
            return item.value;

        return undefined;
    }

    private retrieve<T>(key): Item<T> {
        var item = sessionStorage.getItem(`${this.sessionprefix}.${key}`);
        if (item != null)
            return <Item<T>>JSON.parse(item);

        item = localStorage.getItem(`${this.sessionprefix}.${key}`);
        if (item != null)
            return <Item<T>>JSON.parse(item);

        return undefined;
    }

    private store<T>(key: string, value: T, readonly: boolean, persistent: boolean): boolean {
        try {
            var item: Item<T> = { value: value, readonly: readonly };
            var storage = persistent ? localStorage : sessionStorage;
            storage.setItem(`${this.sessionprefix}.${key}`, JSON.stringify(item));

        } catch (ex) {
            this.logService.info('Session', `item cannot be set for key ${key}, ${ex}`, ex);
            return false;
        }
        return true;
    }

    /**
    * Store a value in the session.
    * @param  {string} key The key to store the value with.
    * @param  {T} value The value to store.
    * @param  {boolean=false} readonly Set value as readonly.
    * @returns boolean A boolean indicating if the value was stored successfully.
    */
    public setValue<T>(key: string, value: T, readonly: boolean = false, persistent: boolean = true): boolean {
        var item = this.retrieve<T>(key);
        if (item && item.readonly)
            throw new Error(`${key} is readonly and cannot be set`);

        return this.store(key, value, readonly, persistent);
    }

    /**
     * Clear a value by key, or all values if key is omitted.
     * @param {string} key Optional key of value to remove.
     */
    public clear(key?: string) {
        if (key) {
            localStorage.removeItem(`${this.sessionprefix}.${key}`);
            sessionStorage.removeItem(`${this.sessionprefix}.${key}`);
        } else {
            var items = [];
            for (var i = 0; i < localStorage.length; i++) {
                var k = localStorage.key(i);
                if (k.indexOf(this.sessionprefix) == 0)
                    items.push(k)
            }
            items.forEach(item => localStorage.removeItem(item));

            items = [];
            for (var i = 0; i < sessionStorage.length; i++) {
                var k = sessionStorage.key(i);
                if (k.indexOf(this.sessionprefix) == 0)
                    items.push(k)
            }
            items.forEach(item => sessionStorage.removeItem(item));
        }
    }
}

interface Item<T> {
    readonly: boolean;
    value: T;
}